/**
 * 分镜看板 API 服务
 * 对接后端 /api/v1/projects/{project_id}/storyboard-board
 */
import apiClient from './client';
import type {
  StoryboardBoardTrackWithItems,
  StoryboardBoardSkeletonData,
  StoryboardBoardPaginatedData,
} from '@/types';

/**
 * 后端 API 响应格式
 */
interface StoryboardBoardApiResponse {
  projectId: string;
  tracks: Array<{
    type: string;
    name: string;
    items: Array<{
      id: string;
      type: string;
      startTime?: number;
      endTime?: number;
      /** summary 视图通用顶层字段 */
      storyboardId?: string;
      entityType?: string;
      entityId?: string | null;
      data: Record<string, any>;
    }>;
  }>;
  totalDuration: number;
  storyboardCount: number;
}

interface StoryboardBoardPaginatedApiResponse {
  tracks?: StoryboardBoardApiResponse['tracks'];
  totalDuration?: number;
  total_duration?: number;
  storyboardCount?: number;
  storyboard_count?: number;
  startIndex?: number;
  start_index?: number;
  endIndex?: number;
  end_index?: number;
}

type StoryboardBoardSkeletonApiResponse = {
  projectId?: string;
  project_id?: string;
  storyboardCount?: number;
  storyboard_count?: number;
  totalDuration?: number;
  total_duration?: number;
  tracks?: Array<{
    type: string;
    name: string;
    items?: Array<{
      id: string;
      orderIndex?: number;
      order_index?: number;
      duration?: number;
    }>;
  }>;
};

const pickField = (obj: Record<string, any>, ...keys: string[]) => {
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeCommonDataFields = (raw: Record<string, any>) => ({
  ...raw,
  storyboardId: pickField(raw, 'storyboardId', 'storyboard_id'),
});

const normalizeResourceDataFields = (raw: Record<string, any>) => ({
  resourceId: pickField(raw, 'resourceId', 'resource_id') ?? null,
  resourceName: pickField(raw, 'resourceName', 'resource_name') ?? null,
  resourceType: pickField(raw, 'resourceType', 'resource_type') ?? null,
  resourceTypeStr: pickField(raw, 'resourceTypeStr', 'resource_type_str') ?? null,
  elementName: pickField(raw, 'elementName', 'element_name') ?? null,
  tagLabel: pickField(raw, 'tagLabel', 'tag_label') ?? null,
  tagId: pickField(raw, 'tagId', 'tag_id') ?? null,
  elementId: pickField(raw, 'elementId', 'element_id') ?? null,
  elementType: pickField(raw, 'elementType', 'element_type') ?? null,
});

const buildTrackTypeParams = (trackTypes?: string[]) => {
  if (!trackTypes || trackTypes.length === 0) return undefined;
  return { trackTypes: trackTypes.join(',') };
};

const normalizeSkeletonResponse = (
  raw: StoryboardBoardSkeletonApiResponse,
  projectId: string
): StoryboardBoardSkeletonData => {
  const resolvedProjectId = raw.projectId ?? raw.project_id ?? projectId;
  const resolvedStoryboardCount = raw.storyboardCount ?? raw.storyboard_count ?? 0;
  const resolvedTotalDuration = raw.totalDuration ?? raw.total_duration ?? 0;
  const resolvedTracks = (raw.tracks ?? []).map((track) => ({
    type: track.type as StoryboardBoardSkeletonData['tracks'][number]['type'],
    name: track.name,
    items: (track.items ?? []).map((item) => ({
      id: item.id,
      orderIndex: item.orderIndex ?? item.order_index ?? 0,
      duration: item.duration ?? 0,
    })),
  }));

  return {
    projectId: resolvedProjectId,
    storyboardCount: resolvedStoryboardCount,
    totalDuration: resolvedTotalDuration,
    tracks: resolvedTracks,
  };
};

const normalizeTracksResponse = (
  projectId: string,
  tracks: StoryboardBoardApiResponse['tracks'] = []
): StoryboardBoardTrackWithItems[] =>
  tracks.map((track, index) => {
    const trackId = `track-${track.type}-${projectId}`;
    return {
      id: trackId,
      projectId,
      type: track.type as StoryboardBoardTrackWithItems['type'],
      name: track.name,
      displayOrder: index,
      visible: true,
      locked: false,
      height: track.type === 'resource' ? 136 : 40,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      items: track.items.map((item, itemIndex) => {
        const rawData = item.data || {};
        const normalizedCommon = normalizeCommonDataFields(rawData);
        const nowIso = new Date().toISOString();

        const itemTypeMap = {
          storyboard: 'storyboard',
          video: 'video',
          sound: 'soundEffect',
          dialogue: 'voiceover',
          voice: 'voiceover',
          resource: 'storyboard',
        } as const;

        const resolvedStoryboardId =
          item.storyboardId ?? pickField(normalizedCommon, 'storyboardId') ?? undefined;
        const resolvedEntityType = item.entityType ?? undefined;
        const resolvedEntityId = item.entityId ?? undefined;

        const baseItem = {
          id: item.id,
          trackId,
          itemType: itemTypeMap[track.type as keyof typeof itemTypeMap] ?? 'storyboard',
          projectId,
          startTime: item.startTime,
          endTime: item.endTime,
          displayOrder: itemIndex,
          createdAt: nowIso,
          updatedAt: nowIso,
          content: '',
          type: item.type,
          storyboardId: resolvedStoryboardId,
          entityType: resolvedEntityType,
          entityId: resolvedEntityId,
        };

        if (track.type === 'storyboard') {
          return {
            ...baseItem,
            ...normalizedCommon,
            storyboardId: resolvedStoryboardId ?? pickField(normalizedCommon, 'id'),
            title: pickField(normalizedCommon, 'title'),
            description: pickField(normalizedCommon, 'description'),
          };
        }
        if (track.type === 'resource') {
          const normalizedResource = normalizeResourceDataFields(rawData);
          return {
            ...baseItem,
            ...normalizedResource,
            resourceId: normalizedResource.resourceId ?? resolvedEntityId ?? undefined,
            storyboardId: resolvedStoryboardId,
            title: pickField(normalizedResource, 'elementName', 'resourceName', 'tagLabel') || '',
          };
        }
        return {
          ...baseItem,
          ...normalizedCommon,
          storyboardId:
            resolvedStoryboardId ?? pickField(normalizedCommon, 'storyboardId') ?? item.id,
          title: pickField(normalizedCommon, 'title', 'name') || '',
        };
      }),
    };
  });

/**
 * 分镜看板 API
 */
export const storyboardBoardApi = {
  /**
   * 获取项目的分镜看板数据
   * GET /api/v1/projects/{project_id}/storyboard-board
   */
  async getByProjectId(
    projectId: string,
    trackTypes?: string[]
  ): Promise<StoryboardBoardTrackWithItems[]> {
    const params: Record<string, string> = {};
    const trackTypeParam = buildTrackTypeParams(trackTypes);
    if (trackTypeParam) Object.assign(params, trackTypeParam);

    const response = (await apiClient.get<StoryboardBoardApiResponse>(
      `/projects/${projectId}/storyboard-board`,
      { params }
    )) as unknown as StoryboardBoardApiResponse;
    return normalizeTracksResponse(projectId, response.tracks ?? []);
  },

  /**
   * 更新分镜看板数据
   * 注意：这是一个临时实现，实际应该调用具体的更新接口
   */
  async update(
    _projectId: string,
    tracks: StoryboardBoardTrackWithItems[]
  ): Promise<StoryboardBoardTrackWithItems[]> {
    // TODO: 实现真实的更新接口
    // 目前返回传入的数据，因为具体的更新操作由其他接口处理
    // 例如：updateStoryboard, reorderStoryboards 等
    console.warn('[storyboardBoardApi] update 方法暂未实现真实 API 调用');
    return tracks;
  },

  /**
   * 获取项目的分镜看板骨架数据（轻量级）
   * GET /api/v1/projects/{project_id}/storyboard-board/skeleton
   */
  async getSkeleton(
    projectId: string,
    trackTypes?: string[],
    startIndex?: number,
    endIndex?: number
  ): Promise<StoryboardBoardSkeletonData> {
    const params: Record<string, string> = {};
    const trackTypeParam = buildTrackTypeParams(trackTypes);
    if (trackTypeParam) Object.assign(params, trackTypeParam);
    if (typeof startIndex === 'number' && typeof endIndex === 'number') {
      params.startIndex = String(startIndex);
      params.endIndex = String(endIndex);
    }
    const response = (await apiClient.get<StoryboardBoardSkeletonApiResponse>(
      `/projects/${projectId}/storyboard-board/skeleton`,
      { params }
    )) as unknown as StoryboardBoardSkeletonApiResponse;
    return normalizeSkeletonResponse(response, projectId);
  },

  /**
   * 获取项目的分镜看板分页数据（基于 orderIndex）
   * GET /api/v1/projects/{project_id}/storyboard-board/paginated
   */
  async getPaginated(
    projectId: string,
    startIndex: number,
    endIndex: number,
    trackTypes?: string[]
  ): Promise<StoryboardBoardPaginatedData> {
    const params: Record<string, string> = {
      startIndex: startIndex.toString(),
      endIndex: endIndex.toString(),
    };

    if (trackTypes && trackTypes.length > 0) {
      params.trackTypes = trackTypes.join(',');
    }

    const response = (await apiClient.get<StoryboardBoardPaginatedApiResponse>(
      `/projects/${projectId}/storyboard-board/paginated`,
      { params }
    )) as unknown as StoryboardBoardPaginatedApiResponse;
    return {
      tracks: response.tracks ? normalizeTracksResponse(projectId, response.tracks) : undefined,
      totalDuration: response.totalDuration ?? response.total_duration ?? 0,
      storyboardCount: response.storyboardCount ?? response.storyboard_count ?? 0,
      startIndex: response.startIndex ?? response.start_index ?? startIndex,
      endIndex: response.endIndex ?? response.end_index ?? endIndex,
    };
  },

  async getPaginatedTracks(
    projectId: string,
    startIndex: number,
    endIndex: number,
    trackTypes?: string[]
  ): Promise<StoryboardBoardTrackWithItems[]> {
    const response = await this.getPaginated(projectId, startIndex, endIndex, trackTypes);
    if (response.tracks && response.tracks.length > 0) {
      return response.tracks;
    }
    return [];
  },

  /**
   * 调整分镜顺序
   * POST /api/v1/projects/{project_id}/storyboard-board/reorder
   */
  async reorder(projectId: string, orderedIds: string[]): Promise<void> {
    const storyboardOrders = orderedIds.reduce<Record<string, number>>(
      (acc, storyboardId, index) => {
        acc[storyboardId] = index;
        return acc;
      },
      {}
    );

    await apiClient.post(`/projects/${projectId}/storyboard-board/reorder`, {
      storyboardOrders,
    });
  },
};
